import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import type { DREData } from "@/hooks/useDREReport";
import { TrendingUp, TrendingDown, DollarSign, Percent } from "lucide-react";
import { cn } from "@/lib/utils";

interface DRESummaryCardsProps {
  data: DREData;
}

function formatCurrency(value: number): string {
  return value.toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

interface MetricCardProps {
  title: string;
  value: number;
  isCurrency?: boolean;
  isPercent?: boolean;
  trend?: "up" | "down" | "neutral";
  description?: string;
}

function MetricCard({ title, value, isCurrency, isPercent, trend, description }: MetricCardProps) {
  const isPositive = value >= 0;
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        {isCurrency && <DollarSign className="h-4 w-4 text-muted-foreground" />}
        {isPercent && <Percent className="h-4 w-4 text-muted-foreground" />}
      </CardHeader>
      <CardContent>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "text-2xl font-bold",
              !isPositive && "text-destructive",
              isPositive && isCurrency && value > 0 && "text-green-600 dark:text-green-400"
            )}
          >
            {isCurrency ? formatCurrency(value) : isPercent ? formatPercent(value) : value}
          </span>
          {TrendIcon && (
            <TrendIcon
              className={cn(
                "h-4 w-4",
                trend === "up" && "text-green-600",
                trend === "down" && "text-destructive"
              )}
            />
          )}
        </div>
        {description && (
          <p className="text-xs text-muted-foreground mt-1">{description}</p>
        )}
      </CardContent>
    </Card>
  );
}

export function DRESummaryCards({ data }: DRESummaryCardsProps) {
  const {
    netRevenue,
    grossProfit,
    grossMarginPercent,
    operatingResult,
    operatingMarginPercent,
    netProfit,
    netMarginPercent,
  } = data;

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
      <MetricCard
        title="Receita Líquida"
        value={netRevenue}
        isCurrency
        description="Vendas - Deduções"
      />

      <MetricCard
        title="Lucro Bruto"
        value={grossProfit}
        isCurrency
        trend={grossProfit > 0 ? "up" : "down"}
        description={`Margem: ${formatPercent(grossMarginPercent)}`}
      />

      <MetricCard
        title="Resultado Operacional"
        value={operatingResult}
        isCurrency
        trend={operatingResult > 0 ? "up" : "down"}
        description={`EBITDA: ${formatPercent(operatingMarginPercent)}`}
      />

      <MetricCard
        title="Lucro Líquido"
        value={netProfit}
        isCurrency
        trend={netProfit > 0 ? "up" : "down"}
        description={`Margem Líquida: ${formatPercent(netMarginPercent)}`}
      />
    </div>
  );
}
